/**
 * TURN CC · 26 (Design refresh) — Kanban card.
 *
 * Read-only pipeline card, aligned on the side panel design system.
 *
 * Layout:
 *   ┌───────────────────────────────────────────┐
 *   │ [type dot] TYPE · HH:MM SGT      [retry]  │  L1 — small caps
 *   │ Title / articles count                     │  L2 — semibold
 *   │ 27 sept. 2026 · 3d ago                     │  L3 — meta muted
 *   │ [status badge]                     [mail]  │  L4 — semantic
 *   └───────────────────────────────────────────┘
 */
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Mail, RefreshCw,
  Clock, Play, Send, CheckCircle2, AlertTriangle,
} from "lucide-react";
import {
  TYPE_META, formatRelative, formatSGTHour,
} from "./calendarMeta";

// Semantic status meta — mirrors EventDetailsPanel PANEL_STATUS.
const CARD_STATUS = {
  scheduled: {
    label: "Scheduled",  Icon: Clock,
    classes:
      "bg-slate-100 text-slate-700 border-slate-200 " +
      "dark:bg-white/5 dark:text-slate-300 dark:border-white/10",
  },
  running: {
    label: "Running",    Icon: Play,
    classes:
      "bg-blue-50 text-blue-700 border-blue-200 " +
      "dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30",
  },
  completed: {
    label: "Ready",      Icon: Send,
    classes:
      "bg-amber-50 text-amber-800 border-amber-200 " +
      "dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/30",
  },
  ready_for_review: {
    label: "Ready",      Icon: Send,
    classes:
      "bg-amber-50 text-amber-800 border-amber-200 " +
      "dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/30",
  },
  delivered: {
    label: "Delivered",  Icon: CheckCircle2,
    classes:
      "bg-emerald-50 text-emerald-700 border-emerald-200 " +
      "dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
  },
  failed: {
    label: "Failed",     Icon: AlertTriangle,
    classes:
      "bg-red-50 text-red-700 border-red-200 " +
      "dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
  },
};

function longDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      timeZone: "Asia/Singapore",
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

export default function KanbanCard({ event, onClick }) {
  const typeMeta = TYPE_META[event.type] || {};
  const TypeIcon = typeMeta.Icon;
  const status = CARD_STATUS[event.status] || CARD_STATUS.scheduled;
  const StatusIcon = status.Icon;
  const isFailed = event.status === "failed";
  const isDelivered = event.status === "delivered";

  const rel = isDelivered && event.email_sent_at
    ? formatRelative(event.email_sent_at)
    : formatRelative(event.starts_at);

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`kanban-card-${event.id}`}
      onClick={() => onClick?.(event)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.(event)}
      className={
        "group cursor-pointer rounded-xl border p-3.5 space-y-1.5 shadow-sm " +
        "border-slate-200 bg-white " +
        "dark:border-white/10 dark:bg-white/5 " +
        "hover:shadow-md hover:border-slate-300 " +
        "dark:hover:bg-white/[0.08] dark:hover:border-white/20 " +
        "transition-all"
      }
    >
      {/* L1 : type dot + type label + hour · retry (if failed) */}
      <div className="flex items-center gap-1.5">
        <span
          className="h-1.5 w-1.5 rounded-full shrink-0"
          style={{ background: typeMeta.color }}
        />
        {TypeIcon && (
          <TypeIcon className="h-3 w-3 text-slate-400 dark:text-slate-500 shrink-0" />
        )}
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 truncate">
          {typeMeta.label}
          <span className="mx-1 opacity-40">·</span>
          <span className="tabular-nums">{formatSGTHour(event.starts_at)} SGT</span>
        </span>
        {isFailed && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid={`kanban-retry-${event.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="ml-auto h-5 w-5 rounded flex items-center justify-center text-red-500 opacity-70 hover:opacity-100 cursor-not-allowed shrink-0"
                >
                  <RefreshCw className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-white/10 text-slate-900 dark:text-slate-100 text-xs"
              >
                Contact admin to retry
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* L2 : title */}
      <div className="text-sm font-semibold text-slate-900 dark:text-white leading-snug line-clamp-2">
        {event.title}
      </div>

      {/* L3 : long date + relative */}
      <div className="text-xs text-slate-500 dark:text-slate-400 flex items-baseline gap-1.5">
        <span>{longDate(event.starts_at)}</span>
        <span className="opacity-40">·</span>
        <span className="tabular-nums">{rel}</span>
      </div>

      {/* L4 : status badge + mail icon if delivered */}
      <div className="flex items-center gap-1.5 pt-0.5">
        <span
          data-testid={`kanban-card-status-${event.id}`}
          className={
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 " +
            "text-[10px] font-semibold border " + status.classes
          }
        >
          <StatusIcon className="h-3 w-3" />
          {status.label}
        </span>
        {isDelivered && event.email_sent_at && (
          <Mail className="h-3 w-3 text-emerald-500/70 dark:text-emerald-400/70 ml-auto shrink-0" />
        )}
      </div>
    </div>
  );
}
